/**
 * Quoted-history stripping for printed email threads.
 *
 * Every reply in a thread quotes the messages before it, so printing the
 * whole thread with full bodies repeats each message many times (a
 * 14-message thread came out at 117 pages). For every message except the
 * oldest, the print drops the quoted part: everything from the first quote
 * marker to the end of the body. The oldest message is never touched, so a
 * forwarded PO at the root of the thread is always printed in full.
 *
 * Markers seen on real ChemMasters threads (verified against staging chains
 * from Gmail, Outlook desktop and Outlook on the web):
 *   - Gmail:            <div class="gmail_quote">  (holds the "On … wrote:" line)
 *   - Outlook web:      <div id="appendonsend"> / <hr> / <div id="divRplyFwdMsg">
 *   - Outlook desktop:  <div style="border-top: …"> whose text starts "From:"
 *   - Apple Mail:       <blockquote type="cite">, preceded by "On … wrote:"
 *   - Plain text:       "On … wrote:", "-----Original Message-----", a
 *                       "From:" line followed by "Sent:" / "Date:"
 *
 * Safety: if stripping would leave no text at all (the sender wrote nothing
 * above the quote, e.g. a bare forward) the body is kept whole, because the
 * quoted part is then the message.
 */

export interface StripResult<T> {
  body: T;
  /** True when a quoted part was found and removed. */
  stripped: boolean;
}

const STRUCTURAL_MARKERS = [
  '.gmail_quote',
  '.gmail_quote_container',
  '#divRplyFwdMsg',
  '#appendonsend',
  'blockquote[type="cite"]',
  '.yahoo_quoted',
  '.moz-cite-prefix',
].join(',');

const WROTE_LINE = /^On\s.{3,200}?\swrote:\s*$/s;
const ORIGINAL_MESSAGE = /^-{2,}\s*Original Message\s*-{2,}/i;
const FORWARDED_MESSAGE = /^-{2,}\s*Forwarded message\s*-{2,}/i;

function ownText(el: Element): string {
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/** Outlook desktop separates the quoted mail with a thin top border above
 *  a From/Sent/To/Subject block. */
function isOutlookHeaderBlock(el: Element): boolean {
  const style = el.getAttribute('style') ?? '';
  if (!/border-top/i.test(style)) return false;
  const text = ownText(el);
  return /^From:/.test(text) && /(Sent|Date):/.test(text);
}

/** A short block whose whole text is a quote lead-in ("On … wrote:", "-----Original Message-----"). */
function isTextLeadIn(el: Element): boolean {
  if (el.children.length > 3) return false;
  const text = ownText(el);
  if (text.length === 0 || text.length > 260) return false;
  return WROTE_LINE.test(text) || ORIGINAL_MESSAGE.test(text) || FORWARDED_MESSAGE.test(text);
}

function firstInDocumentOrder(candidates: Element[]): Element | null {
  let first: Element | null = null;
  for (const el of candidates) {
    if (!first || el.compareDocumentPosition(first) & Node.DOCUMENT_POSITION_FOLLOWING) {
      first = el;
    }
  }
  return first;
}

/** Remove `marker` and every node after it in document order (within body). */
function truncateAt(marker: Element, body: HTMLElement): void {
  // Outlook web puts an <hr> right before the reply header: drop it too.
  const prev = marker.previousElementSibling;
  if (prev && prev.tagName === 'HR') prev.remove();

  let node: Node | null = marker;
  while (node && node !== body) {
    let sib = node.nextSibling;
    while (sib) {
      const next = sib.nextSibling;
      sib.parentNode?.removeChild(sib);
      sib = next;
    }
    node = node.parentNode;
  }
  marker.remove();
}

function findQuoteMarker(body: HTMLElement): Element | null {
  const candidates: Element[] = Array.from(body.querySelectorAll(STRUCTURAL_MARKERS));

  for (const el of Array.from(body.querySelectorAll('div, p, span, td'))) {
    if (isOutlookHeaderBlock(el) || isTextLeadIn(el)) candidates.push(el);
  }
  // A lead-in nested inside a wider block: prefer the outermost candidate so
  // the whole quoted container goes, but only when that container is itself
  // a candidate; otherwise the lead-in element is the cut point.
  return firstInDocumentOrder(candidates);
}

/** HTML body with the quoted history removed. Needs a DOM (browser / jsdom). */
export function stripQuotedHtml(html: string): StripResult<string> {
  if (!html || !html.trim()) return { body: html, stripped: false };
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  if (!body) return { body: html, stripped: false };

  const marker = findQuoteMarker(body);
  if (!marker || marker === body) return { body: html, stripped: false };

  const before = ownText(body);
  truncateAt(marker, body);
  const after = ownText(body);
  if (after.length === 0 || after.length === before.length) {
    return { body: html, stripped: false };
  }
  return { body: body.innerHTML, stripped: true };
}

const TEXT_SEPARATOR = /^_{8,}\s*$/;

/** Plain-text body with the quoted history removed. */
export function stripQuotedText(text: string): StripResult<string> {
  if (!text || !text.trim()) return { body: text, stripped: false };
  const lines = text.split(/\r?\n/);
  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (WROTE_LINE.test(line) || ORIGINAL_MESSAGE.test(line) || FORWARDED_MESSAGE.test(line)) {
      cut = i;
      break;
    }
    if (TEXT_SEPARATOR.test(line) && lines.slice(i + 1, i + 4).some((l) => /^From:/.test(l.trim()))) {
      cut = i;
      break;
    }
    if (/^From:\s/.test(line) && lines.slice(i + 1, i + 5).some((l) => /^(Sent|Date):\s/.test(l.trim()))) {
      cut = i;
      break;
    }
    if (/^>/.test(line) && lines.slice(i).filter((l) => l.trim()).every((l) => /^>/.test(l.trim()))) {
      cut = i;
      break;
    }
  }
  if (cut <= 0) return { body: text, stripped: false };
  const kept = lines.slice(0, cut).join('\n').replace(/\s+$/, '');
  if (!kept.trim()) return { body: text, stripped: false };
  return { body: kept, stripped: true };
}
